import ListCard from '@Components/ListCard/ListCard';
import ReportModal from '@Components/Modals/Report/ReportModal';
import ReportService from '@Services/Report.services.ts';
import { Report } from '@Types/Report.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { getDateString } from '@Utils/date.utils.ts';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Reports.module.scss';

const Reports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState<PaginatedResponse<Report>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useTopHat({
    activeNav: 'nav-reports',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      {
        title: 'Reports',
        href: `${PATHS.REPORTS}?page=${1}`,
        id: 'Reports',
      },
    ],
  });

  const getReports = () => {
    setIsLoading(true);
    ReportService.getReportsSearch({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setReports(data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => getReports(), [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleNewPage = (newPage: number) => {
    if (newPage <= 0 || newPage > reports.totalPages) {
      return;
    }
    searchParams.set(PAGE_PROP, newPage.toString());
    setSearchParams(searchParams);
  };

  const handleOnCreateReportSuccess = () => {
    setShowCreateModal(false);
    getReports();
  };

  return (
    <div>
      <div className='grid'>
        <div className='col-12 col-md-3'>
          <SectionCard className={styles['reports-actions-card']}>
            <SearchBox
              id='search-reports'
              fullWidth
              focusOnRender
              value={searchQuery}
              defaultValue={searchQuery}
              onChange={handleSearchQueryChange}
            />
            <Button type='primary' className='button-with-icon' fullWidth onClick={() => setShowCreateModal(true)}>
              {Icons.Plus} New Report
            </Button>
            <br />
            <Button onClick={() => navigate(PATHS.SCANNER_LOG)} type='secondary' className='button-with-icon' fullWidth>
              <i className='fa-solid fa-eye' /> Show Scanner Logs
            </Button>
          </SectionCard>
        </div>
        <div className='col-12 col-md-9'>
          <ListCard
            title="Reports"
            structure={TABLE_STRUCTURES.REPORTS}
            data={reports.docs.map((report) => ({
              ...report,
              createdAt: getDateString(report.createdAt),
            }))}
            detailsPath={PATHS.REPORTS}
            loading={isLoading}
            withDetails
            error={errorMessage}
            searchEntered={!!searchQuery}
          />
        </div>
        <div className='col-12 content-middle content-right-md'>
          <Pagination
            count={reports.totalDocs}
            perPage={PAGE_SIZE}
            currentPage={currentPage - 1}
            onChange={(page) => handleNewPage(page + 1)}
          />
        </div>
      </div>
      <ReportModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => handleOnCreateReportSuccess()}
      />
    </div>
  );
};

export default Reports;
